<template>
  <aside class="rd" :class="{ 'is-open': open, 'is-resizing': resizing, 'is-floating': $store.state.panel.drawerFloats }" aria-label="Side panel">
    <!-- Drag the left edge to make the panel wider or narrower; double-click puts it back. -->
    <div class="rd-grip" v-if="!$store.state.panel.mobile" title="Drag to resize · double-click to reset" @mousedown.prevent="startResize" @touchstart.prevent="startResize" @dblclick="resetWidth"></div>
    <template v-if="open">
    <div class="rd-head">
      <div class="rd-head-top">
        <!-- the open tab's mark: approvals, the team chat, or Connect's sparkle -->
        <span class="rd-head-icon" aria-hidden="true">
          <svg v-if="tab === 'approvals'" class="rd-svg" viewBox="0 0 24 24"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>
          <svg v-else-if="tab === 'chat'" class="rd-svg" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
          <svg v-else class="rd-svg is-filled" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" /></svg>
        </span>
        <div class="rd-head-text">
          <div class="rd-head-title">{{ tab === "approvals" ? $tr("Approvals") : tab === "chat" ? $tr("Team chat") : $tr("Connect") }}</div>
          <div class="rd-head-state" v-if="tab === 'approvals' && approvalsCount"><span class="rd-state-dot is-warn"></span>{{ approvalsCount }} waiting for you</div>
          <div class="rd-head-state" v-else-if="tab === 'approvals'"><span class="rd-state-dot is-good"></span>Nothing is waiting for you</div>
          <div class="rd-head-state" v-else-if="tab === 'assistant' && assistant.busy"><span class="rd-state-dot is-busy"></span>Thinking…</div>
          <div class="rd-head-state" v-else-if="tab === 'assistant' && !assistant.loaded">Connecting…</div>
          <div class="rd-head-state" v-else-if="tab === 'assistant' && !assistant.enabled"><span class="rd-state-dot"></span>Not switched on</div>
          <div class="rd-head-state" v-else-if="tab === 'assistant'"><span class="rd-state-dot is-good"></span>Ready</div>
          <div class="rd-head-state" v-else-if="$store.getters.chatPresence"><span class="rd-state-dot is-good"></span>{{ $store.getters.chatPresence }}</div>
        </div>
        <button type="button" class="rd-close" title="Close" aria-label="Close" @click="$store.dispatch('closeDrawer')"><AppIcon name="x" /></button>
      </div>
      <div class="rd-tabs">
        <button type="button" class="rd-tab" :class="{ 'is-on': tab === 'approvals' }" @click="setTab('approvals')">
          Approvals <span class="rd-count" v-if="approvalsCount">{{ approvalsCount }}</span>
        </button>
        <button type="button" class="rd-tab" :class="{ 'is-on': tab === 'chat' }" @click="setTab('chat')">
          Chat <span class="rd-count" v-if="chatUnread">{{ chatUnread }}</span>
        </button>
        <button type="button" class="rd-tab" v-if="assistant.allowed" :class="{ 'is-on': tab === 'assistant' }" @click="setTab('assistant')" title="Connect, the assistant: ask it anything about the panel">
          Connect
        </button>
      </div>
    </div>

    <!-- ============================ APPROVALS ============================ -->
    <div class="rd-body" v-if="tab === 'approvals'">
      <p class="rd-lede">Things other people wrote or asked for that need a yes from you before they happen.</p>

      <template v-if="notifs.approver">
        <div class="rd-section-title">Notifications waiting <span class="rd-section-count" v-if="notifs.pending.length">{{ notifs.pending.length }}</span></div>
        <div v-if="!notifs.pending.length" class="rd-empty">
          <span class="rd-empty-mark" aria-hidden="true"><svg class="rd-svg" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg></span>
          No notification is waiting.
        </div>
        <ul v-else class="rd-list">
          <li v-for="n in notifs.pending" :key="'n-' + n.id" class="rd-item">
            <div class="rd-item-top">
              <span class="rd-tile" aria-hidden="true"><AppIcon name="bell" /></span>
              <StatusBadge label="Push notification" tone="amber" />
              <span class="rd-item-when">{{ timeAgo(n.created_at) }}</span>
            </div>
            <div class="rd-item-title">{{ n.title }}</div>
            <div class="rd-item-sub">{{ n.body }}</div>
            <div class="rd-item-sub">by <b>{{ n.created_by_name || $trf("User #{0}", [n.created_by]) }}</b> · {{ n.type_label }} · {{ n.target_label }}<span v-if="n.coupon_code"> · coupon {{ n.coupon_code }}</span></div>
            <img v-if="n.image_url" :src="n.image_url" class="rd-item-img" />
            <ReasonConfirm
              v-if="pendingNotif && pendingNotif.hash === n.notification_hash"
              class="rd-confirm"
              :title="pendingNotif.decision === 'approve' ? $trf('Approve “{0}”?', [n.title]) : $trf('Reject “{0}”?', [n.title])"
              :text="pendingNotif.decision === 'approve' ? $tr('It goes out to every customer at its scheduled time, or on the sender’s next run if that time has passed.') : $tr('The writer gets your note in a direct message.')"
              :confirm-label="pendingNotif.decision === 'approve' ? $tr('Approve') : $tr('Reject')"
              :ask-reason="pendingNotif.decision === 'reject'"
              :reason-required="false"
              :danger="pendingNotif.decision === 'reject'"
              @confirm="confirmNotif"
              @cancel="pendingNotif = null"
            />
            <div class="rd-actions" v-else>
              <button type="button" class="rd-btn rd-btn--go" @click="pendingNotif = { hash: n.notification_hash, decision: 'approve' }">Approve</button>
              <button type="button" class="rd-btn" @click="pendingNotif = { hash: n.notification_hash, decision: 'reject' }">Reject</button>
            </div>
          </li>
        </ul>
      </template>

      <template v-if="changes.approver">
        <div class="rd-section-title">Connect changes waiting <span class="rd-section-count" v-if="changes.pending.length">{{ changes.pending.length }}</span></div>
        <div v-if="!changes.pending.length" class="rd-empty">
          <span class="rd-empty-mark" aria-hidden="true"><svg class="rd-svg" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg></span>
          No Connect change is waiting.
        </div>
        <ul v-else class="rd-list">
          <li v-for="c in changes.pending" :key="'c-' + c.id" class="rd-item">
            <div class="rd-item-top">
              <span class="rd-tile is-connect" aria-hidden="true"><svg class="rd-svg is-filled" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" /></svg></span>
              <StatusBadge label="Connect change" tone="amber" />
              <span class="rd-item-when">{{ timeAgo(c.sent_at || c.created_at) }}</span>
            </div>
            <div class="rd-item-title">{{ c.asker_name }} asks Connect to</div>
            <div class="rd-item-sub">{{ c.summary }}</div>
            <div class="rd-item-sub" v-if="c.danger">{{ c.danger === 1 ? $tr("It deletes or takes away 1 thing.") : $trf("It deletes or takes away {0} things.", [c.danger]) }}</div>
            <ReasonConfirm
              v-if="pendingChange && pendingChange.id === c.id"
              class="rd-confirm"
              :title="pendingChange.decision === 'approve' ? $tr('Approve it and run it now?') : $tr('Reject it?')"
              :text="pendingChange.decision === 'approve' ? $trf('It runs now with {0}’s permissions and stores; they see the result in their chat with Connect.', [c.asker_name]) : $trf('{0} gets your note in their chat with Connect.', [c.asker_name])"
              :confirm-label="pendingChange.decision === 'approve' ? $tr('Approve') : $tr('Reject')"
              :ask-reason="pendingChange.decision === 'reject'"
              :reason-required="false"
              :danger="pendingChange.decision === 'reject'"
              @confirm="confirmChange"
              @cancel="pendingChange = null"
            />
            <div class="rd-actions" v-else>
              <button type="button" class="rd-btn rd-btn--go" @click="pendingChange = { id: c.id, decision: 'approve' }">Approve</button>
              <button type="button" class="rd-btn" @click="pendingChange = { id: c.id, decision: 'reject' }">Reject</button>
            </div>
          </li>
        </ul>
      </template>

      <template v-if="blog.approver">
        <div class="rd-section-title">Website blog changes waiting <span class="rd-section-count" v-if="blog.pending.length">{{ blog.pending.length }}</span></div>
        <div v-if="!blog.pending.length" class="rd-empty">
          <span class="rd-empty-mark" aria-hidden="true"><svg class="rd-svg" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg></span>
          No website blog change is waiting.
        </div>
        <ul v-else class="rd-list">
          <li v-for="b in blog.pending" :key="'b-' + b.id" class="rd-item">
            <div class="rd-item-top">
              <span class="rd-tile" aria-hidden="true"><AppIcon name="ic-blog" /></span>
              <StatusBadge label="Website blog" tone="amber" />
              <span class="rd-item-when">{{ timeAgo(b.updated_at || b.created_at) }}</span>
            </div>
            <div class="rd-item-title">{{ b.via === "connect" ? $trf("{0} asks through Connect to", [b.asker_name]) : $trf("{0} asks to", [b.asker_name]) }}</div>
            <div class="rd-item-sub">{{ b.summary }}</div>
            <img v-if="b.card_image" :src="b.card_image" class="rd-item-img" loading="lazy" />
            <div class="rd-item-sub rd-item-warn" v-if="b.gone"><AppIcon name="alert-circle" />The story is no longer on the website. Reject it with a note.</div>
            <div class="rd-item-sub rd-item-warn" v-else-if="b.conflicts.length"><AppIcon name="alert-circle" />The website’s {{ b.conflict_labels }} changed after {{ b.asker_name }} asked, so approving would undo that. Reject it with a note and they can ask again.</div>
            <ReasonConfirm
              v-if="pendingBlog && pendingBlog.id === b.id"
              class="rd-confirm"
              :title="pendingBlog.decision === 'approve' ? (b.action === 'delete' ? $tr('Approve it and take the story off the website now?') : $tr('Approve it and put it on the website now?')) : $tr('Reject it?')"
              :text="pendingBlog.decision === 'approve' ? b.summary : $trf('{0} gets your note in a direct message.', [b.asker_name])"
              :confirm-label="pendingBlog.decision === 'approve' ? $tr('Approve') : $tr('Reject')"
              :ask-reason="pendingBlog.decision === 'reject'"
              :reason-required="false"
              :danger="pendingBlog.decision === 'reject'"
              @confirm="confirmBlog"
              @cancel="pendingBlog = null"
            />
            <div class="rd-actions" v-else>
              <button type="button" class="rd-btn rd-btn--go" :disabled="b.gone || b.conflicts.length > 0" @click="pendingBlog = { id: b.id, decision: 'approve' }">Approve</button>
              <button type="button" class="rd-btn" @click="pendingBlog = { id: b.id, decision: 'reject' }">Reject</button>
              <button type="button" class="rd-btn" @click="reviewBlog(b)">Review</button>
            </div>
          </li>
        </ul>
      </template>

      <template v-if="requests.approver">
        <div class="rd-section-title">Access requests <span class="rd-section-count" v-if="requests.pending.length">{{ requests.pending.length }}</span></div>
        <div v-if="!requests.pending.length" class="rd-empty">
          <span class="rd-empty-mark" aria-hidden="true"><svg class="rd-svg" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg></span>
          Nobody is waiting for access.
        </div>
        <ul v-else class="rd-list">
          <li v-for="r in requests.pending" :key="'p-' + r.id" class="rd-item">
            <div class="rd-item-top">
              <span class="rd-tile" aria-hidden="true"><AppIcon name="ic-users" /></span>
              <StatusBadge label="Access request" tone="amber" />
              <span class="rd-item-when">{{ timeAgo(r.created_at) }}</span>
            </div>
            <div class="rd-item-title">{{ r.from_name }} asks for <em>{{ r.req_role_name }}</em></div>
            <div class="rd-item-sub">“{{ r.body }}”</div>
            <ReasonConfirm
              v-if="pending && pending.id === r.id"
              class="rd-confirm"
              :title="pending.decision === 'approved' ? $trf('Give {0} the {1} role?', [r.from_name, r.req_role_name]) : $trf('Decline {0}’s request?', [r.from_name])"
              :text="pending.decision === 'approved' ? $tr('Same as assigning it on the Users page. They will see it after signing in again.') : $tr('They get your note in a direct message.')"
              :confirm-label="pending.decision === 'approved' ? $tr('Approve') : $tr('Decline')"
              :ask-reason="pending.decision === 'declined'"
              :reason-required="false"
              :danger="pending.decision === 'declined'"
              @confirm="confirmResolve"
              @cancel="pending = null"
            />
            <div class="rd-actions" v-else>
              <button type="button" class="rd-btn rd-btn--go" @click="pending = { id: r.id, decision: 'approved' }">Approve</button>
              <button type="button" class="rd-btn" @click="pending = { id: r.id, decision: 'declined' }">Decline</button>
            </div>
          </li>
        </ul>
      </template>

      <div class="rd-section-title">
        <span>Your notifications</span>
        <button type="button" class="rd-link" v-if="rejectedMine.length > 1" @click="clearRejected">Clear {{ rejectedMine.length }} not approved</button>
      </div>
      <div v-if="!notifs.mine.length" class="rd-muted">Nothing written yet. New notifications wait here for approval before they go out.</div>
      <ul v-else class="rd-list rd-list--quiet">
        <li v-for="n in notifs.mine" :key="'mn-' + n.id" class="rd-item">
          <div class="rd-item-top">
            <span class="rd-tile" aria-hidden="true"><AppIcon name="bell" /></span>
            <StatusBadge :label="notifLabel(n.approval)" :tone="notifTone(n.approval)" />
            <span class="rd-item-when">{{ timeAgo(n.created_at) }}</span>
          </div>
          <div class="rd-item-title">{{ n.title }}</div>
          <div class="rd-item-sub" v-if="n.approval === 'rejected'">Not approved<span v-if="n.reviewed_by_name"> by {{ n.reviewed_by_name }}</span><span v-if="n.review_note">: “{{ n.review_note }}”</span></div>
          <div class="rd-item-sub" v-else-if="n.approval === 'approved'">Approved<span v-if="n.reviewed_by_name"> by {{ n.reviewed_by_name }}</span> — goes out to customers at its scheduled time.</div>
          <div class="rd-actions" v-if="n.approval !== 'sent'">
            <button type="button" class="rd-btn rd-btn--sm" :disabled="removing === n.notification_hash" @click="removeNotif(n)" title="Delete this draft">Remove</button>
          </div>
        </li>
      </ul>

      <template v-if="blog.mine.length">
        <div class="rd-section-title">Your website blog changes</div>
        <ul class="rd-list rd-list--quiet">
          <li v-for="b in blog.mine" :key="'mb-' + b.id" class="rd-item">
            <div class="rd-item-top">
              <span class="rd-tile" aria-hidden="true"><AppIcon name="ic-blog" /></span>
              <StatusBadge :label="blogLabel(b.status)" :tone="blogTone(b.status)" />
              <span class="rd-item-when">{{ timeAgo(b.decided_at || b.updated_at || b.created_at) }}</span>
            </div>
            <div class="rd-item-title">{{ b.summary }}</div>
            <div class="rd-item-sub" v-if="b.status === 'rejected'">Not approved<span v-if="b.decided_by_name"> by {{ b.decided_by_name }}</span><span v-if="b.note">: “{{ b.note }}”</span></div>
            <div class="rd-item-sub" v-else-if="b.status === 'approved'">Approved<span v-if="b.decided_by_name"> by {{ b.decided_by_name }}</span> — {{ b.action === "delete" ? "the story is off the website." : "it is on the website." }}</div>
            <div class="rd-item-sub" v-else-if="b.status === 'closed'">Closed: {{ b.note }}</div>
            <div class="rd-item-sub rd-item-warn" v-else-if="b.status === 'pending' && b.conflicts.length"><AppIcon name="alert-circle" />The website’s {{ b.conflict_labels }} changed since you asked; open the story and make your change again.</div>
            <div class="rd-actions" v-if="b.status === 'pending'">
              <button type="button" class="rd-btn rd-btn--sm" @click="reviewBlog(b)">Open</button>
              <button type="button" class="rd-btn rd-btn--sm" :disabled="withdrawing === b.id" @click="withdrawBlog(b)">Withdraw</button>
            </div>
          </li>
        </ul>
      </template>

      <div class="rd-section-title">Your access requests</div>
      <div v-if="!requests.mine.length" class="rd-muted">You have not asked for anything. Use <b>Request access</b> in the Chat tab.</div>
      <ul v-else class="rd-list rd-list--quiet">
        <li v-for="r in requests.mine" :key="'m-' + r.id" class="rd-item">
          <div class="rd-item-top">
            <span class="rd-tile" aria-hidden="true"><AppIcon name="ic-users" /></span>
            <StatusBadge :label="reqLabel(r.req_status)" :tone="reqTone(r.req_status)" />
            <span class="rd-item-when">{{ timeAgo(r.created_at) }}</span>
          </div>
          <div class="rd-item-title">{{ r.req_role_name }}</div>
          <div class="rd-item-sub">“{{ r.body }}”<span v-if="r.resolved_by_name"> · {{ r.req_status }} by {{ r.resolved_by_name }}</span></div>
        </li>
      </ul>
    </div>

    <!-- ============================== CHAT ============================== -->
    <div class="rd-body rd-body--chat" v-else>
      <!-- end-to-end encryption: the one card that stands between a person and their messages -->
      <div class="rd-form rd-e2ee" v-if="chat.loaded && !chat.error && e2eeCard && !isAssistantChannel">
        <div class="rd-form-head">
          <span class="rd-tile" aria-hidden="true"><svg class="rd-svg" viewBox="0 0 24 24"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg></span>
          <div class="rd-form-title">End-to-end encryption</div>
        </div>
        <template v-if="chat.e2ee.status === 'setup'">
          <p class="rd-muted">Messages are encrypted in your browser; the server only ever stores ciphertext. Choose a passphrase so you can open your messages from another browser or phone. It cannot be recovered for you.</p>
          <input class="rd-input" type="password" v-model="e2eePass" placeholder="Passphrase (8 characters or more)" autocomplete="new-password" />
          <input class="rd-input" type="password" v-model="e2eePass2" placeholder="Repeat it" autocomplete="new-password" @keydown.enter.prevent="e2eeSetup(false)" />
          <div class="rd-error" v-if="chat.e2ee.error"><AppIcon name="alert-circle" />{{ chat.e2ee.error }}</div>
          <div class="rd-actions">
            <button type="button" class="rd-btn rd-btn--go" :disabled="chat.e2ee.busy || e2eePass.length < 8 || e2eePass !== e2eePass2" @click="e2eeSetup(false)">{{ e2eeResetting ? "Make the new key" : "Turn on" }}</button>
            <button type="button" class="rd-btn" :disabled="chat.e2ee.busy" title="No passphrase: your messages open in this browser only" @click="e2eeSetup(true)">This browser only</button>
          </div>
          <p class="rd-muted rd-e2ee-help"><b>What is the passphrase?</b> A phrase you make up now, like a second password for the chat only. You type it once here; it is asked again only when you open the chat on another browser or phone. <b>Forgot it?</b> Nothing is lost: on the new device press <b>New key</b>. New messages open at once, and the older ones come back as soon as a colleague's panel is open.</p>
        </template>
        <template v-else-if="chat.e2ee.status === 'unlock'">
          <p class="rd-muted" v-if="chat.e2ee.hasBackup">Your chat key was made on another browser. Enter the passphrase you chose there to use it here.</p>
          <p class="rd-muted" v-else>Your chat key was made on another browser and has no passphrase backup, so the only way here is a new key.</p>
          <p class="rd-muted rd-e2ee-help"><b>Forgot the passphrase?</b> Press <b>New key</b>. New messages open at once; older ones come back as soon as a colleague's panel is open. Nothing is lost while one person on the team still has their key.</p>
          <input v-if="chat.e2ee.hasBackup" class="rd-input" type="password" v-model="e2eePass" placeholder="Passphrase" autocomplete="current-password" @keydown.enter.prevent="e2eeUnlock" />
          <div class="rd-error" v-if="chat.e2ee.error"><AppIcon name="alert-circle" />{{ chat.e2ee.error }}</div>
          <div class="rd-actions">
            <button type="button" class="rd-btn rd-btn--go" v-if="chat.e2ee.hasBackup" :disabled="chat.e2ee.busy || !e2eePass" @click="e2eeUnlock">Unlock</button>
            <button type="button" class="rd-btn" :disabled="chat.e2ee.busy" @click="e2eeResetAsk = true">New key…</button>
          </div>
          <ReasonConfirm
            v-if="e2eeResetAsk"
            title="Make a new key?"
            text="Your old backup stops working. Messages people sent before now open again once one of them has the panel open (it re-shares the channel keys with your new key)."
            confirmLabel="New key"
            :askReason="false"
            danger
            @confirm="e2eeReset"
            @cancel="e2eeResetAsk = false"
          />
        </template>
        <template v-else-if="chat.e2ee.status === 'nocrypto'">
          <p class="rd-muted">This browser cannot do the encryption (no Web Crypto). Use a current Chrome, Safari, Firefox or Edge over https.</p>
        </template>
      </div>
      <div class="rd-error" v-if="chat.error"><AppIcon name="alert-circle" />{{ chat.error }}</div>
      <div v-else-if="!chat.loaded" class="rd-muted">Connecting…</div>


      <!-- channel list -->
      <template v-else-if="!chat.active">
        <div class="rd-section-title">Channels</div>
        <ul class="rd-chan">
          <li v-for="c in channelList" :key="c.key" class="rd-chan-row" :class="{ 'is-unread': c.unread }" role="button" tabindex="0" @click="openChannel(c.key)" @keydown.enter.prevent="openChannel(c.key)">
            <span class="rd-chan-mark">{{ c.kind === 'everyone' ? '#' : c.kind === 'role' ? '@' : '·' }}</span>
            <span class="rd-chan-name">{{ c.name }}</span>
            <span class="rd-chan-kind" v-if="c.kind === 'role'">role</span>
            <span class="rd-dot" v-if="c.unread">{{ c.unread }}</span>
          </li>
        </ul>

        <div class="rd-section-title">Direct messages</div>
        <ul class="rd-chan" v-if="dmList.length">
          <li v-for="c in dmList" :key="c.key" class="rd-chan-row" :class="{ 'is-unread': c.unread }" role="button" tabindex="0" @click="openChannel(c.key)" @keydown.enter.prevent="openChannel(c.key)">
            <span class="rd-avatar" :class="{ 'is-bot': c.bot }" aria-hidden="true">{{ (c.name || "?").charAt(0) }}<span class="rd-presence" :class="{ 'is-on': online(c.other_uid) }"></span></span>
            <span class="rd-chan-name">{{ c.name }}</span>
            <span class="rd-dot" v-if="c.unread">{{ c.unread }}</span>
          </li>
        </ul>
        <div class="rd-muted" v-else>No direct messages yet — pick someone below.</div>

        <div class="rd-section-title">People</div>
        <ul class="rd-chan">
          <li v-for="p in chat.people" :key="'u' + p.uid" class="rd-chan-row" role="button" tabindex="0" @click="$store.dispatch('chatOpenDm', p)" @keydown.enter.prevent="$store.dispatch('chatOpenDm', p)">
            <span class="rd-avatar" aria-hidden="true">{{ (p.name || "?").charAt(0) }}<span class="rd-presence" :class="{ 'is-on': online(p.uid) }"></span></span>
            <span class="rd-chan-name" :title="p.fp ? $trf('Key {0}', [p.fp]) : (chat.e2ee.enabled ? $tr('No encryption key yet: they have not opened the chat') : '')">{{ p.name }}</span>
            <span class="rd-chan-kind">{{ p.role }}</span>
          </li>
        </ul>
        <div class="rd-muted rd-e2ee-note" v-if="chat.e2ee.status === 'ready'">🔒 Encrypted end to end · your key {{ chat.e2ee.fp }}</div>

        <div class="rd-foot-actions">
          <button type="button" class="rd-btn" @click="requestOpen = !requestOpen; groupOpen = false">Request access…</button>
          <button type="button" class="rd-btn" v-if="chat.me && chat.me.can_manage_groups" @click="groupOpen = !groupOpen; requestOpen = false">New group…</button>
        </div>

        <div class="rd-form" v-if="requestOpen">
          <div class="rd-form-title">Ask for a role</div>
          <select class="rd-input" v-model="reqRole">
            <option value="">Which role?</option>
            <option v-for="r in chat.roles" :key="r.key" :value="r.key" :disabled="chat.me && r.key === chat.me.role">{{ r.name }}</option>
          </select>
          <textarea class="rd-input" rows="3" v-model.trim="reqReason" :placeholder="$tr('Why do you need it? One sentence is enough.')"></textarea>
          <div class="rd-actions">
            <button type="button" class="rd-btn" @click="requestOpen = false">Cancel</button>
            <button type="button" class="rd-btn rd-btn--go" :disabled="!reqRole || !reqReason" @click="sendRequest">Send request</button>
          </div>
        </div>

        <div class="rd-form" v-if="groupOpen">
          <div class="rd-form-title">New group</div>
          <input class="rd-input" v-model.trim="groupName" placeholder="Group name" />
          <div class="rd-check-grid">
            <label class="rd-check" v-for="p in chat.people" :key="'g' + p.uid"><input type="checkbox" :value="p.uid" v-model="groupMembers" /> {{ p.name }}</label>
          </div>
          <div class="rd-actions">
            <button type="button" class="rd-btn" @click="groupOpen = false">Cancel</button>
            <button type="button" class="rd-btn rd-btn--go" :disabled="!groupName" @click="createGroup">Create</button>
          </div>
        </div>
      </template>

      <!-- conversation -->
      <template v-else>
        <div class="rd-conv-head" :class="{ 'is-assistant': isAssistantChannel }">
          <button type="button" class="rd-back" v-if="tab !== 'assistant'" aria-label="Back" @click="$store.commit('SET_CHAT_ACTIVE', '')"><AppIcon name="chevron-left" /></button>
          <div class="rd-conv-name">{{ activeName }}<span class="rd-bot-sub" v-if="isAssistantChannel"> · reads the panel for you; write @connect in any channel to call it there<template v-if="chat.e2ee.enabled">. {{ $tr("Not encrypted: Connect has to read it") }}</template></span><span class="rd-bot-sub" v-else-if="activeEncrypted" title="Encrypted in your browser; the server stores ciphertext"> · 🔒 end-to-end encrypted</span></div>
          <button type="button" class="rd-link rd-conv-act" v-if="activeIsGroup && chat.me && chat.me.can_manage_groups" @click="archiveAsk = true" title="Delete this group">Delete group</button>
          <button type="button" class="rd-switch" v-if="isAssistantChannel && assistant.canSkipConfirm" :class="{ 'is-off': assistant.skipConfirm }" :aria-pressed="assistant.skipConfirm ? 'false' : 'true'" :disabled="switching" @click="toggleSkipConfirm"
                  title="On: every change Connect prepares waits for your Confirm. Off: it runs as soon as you ask; a delete still asks you to type how many go, and Undo stays on the card.">
            {{ assistant.skipConfirm ? $tr("Ask before changes: Off") : $tr("Ask before changes: On") }}
          </button>
          <button type="button" class="rd-switch rd-files-btn" v-if="isAssistantChannel && modelName && assistant.modelCanChoose" :class="{ 'is-on': showModel }" :aria-pressed="showModel ? 'true' : 'false'" @click="showModel = !showModel; showFiles = false"
                  title="Which model answers you (/model)">
            <svg class="rd-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /></svg>
            {{ modelName }}
          </button>
          <button type="button" class="rd-switch rd-files-btn" v-if="isAssistantChannel" :class="{ 'is-on': showFiles }" :aria-pressed="showFiles ? 'true' : 'false'" @click="showFiles = !showFiles; showModel = false"
                  title="The PDF reports and Excel files Connect made for you">
            <svg class="rd-svg" viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>
            Files
          </button>
        </div>
        <ReasonConfirm
          v-if="archiveAsk"
          class="rd-conv-confirm"
          title="Delete this group?"
          :text="$trf('“{0}” disappears from everyone’s list. Its messages stay in the database for the record.', [activeName])"
          confirmLabel="Delete group"
          :askReason="false"
          danger
          @confirm="archiveGroup"
          @cancel="archiveAsk = false"
        />
        <div class="rd-conv" ref="conv" @scroll="onScroll">
          <button type="button" class="rd-more" v-if="chat.hasMore[chat.active]" @click="$store.dispatch('chatLoadMore', chat.active)">Load earlier</button>
          <div v-if="!activeMessages.length && isAssistantChannel" class="rd-welcome">
            <span class="rd-welcome-icon" aria-hidden="true"><svg class="rd-svg is-filled" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" /></svg></span>
            <button v-if="canTalk && !talking" type="button" class="rd-welcome-talk" @click="startTalking">
              <span class="rd-voice-orb" aria-hidden="true"><svg class="rd-svg" viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></svg></span>
              <span class="rd-welcome-talk-text"><b>Talk with Connect</b><small>Ask out loud in any language. It answers by voice.</small></span>
            </button>
            <p class="rd-welcome-text">Or type in Turkish or English. It answers from the same numbers the pages show, with your permissions, and can draw charts and make PDF reports and Excel files.</p>
            <div class="rd-chips" v-if="chips.length">
              <button type="button" class="rd-chip" v-for="c in chips" :key="c" :disabled="assistant.busy" @click="draft = c; send()">{{ c }}</button>
            </div>
          </div>
          <div v-else-if="!activeMessages.length" class="rd-conv-empty">Nothing here yet. Say hello.</div>
          <template v-for="{ m, when } in rows" :key="m.id">
            <div v-if="m.kind === 'system'" class="rd-sys">{{ m.body }}</div>
            <ChangeSetCard
              v-else-if="m.kind === 'proposal' && m.proposal && m.proposal.args && m.proposal.args.cs"
              :m="m" :when="when" :busy="deciding === m.id"
              :mine="!!(chat.me && m.proposal.asker === chat.me.uid)"
              @decide="decide"
            />
            <div v-else-if="m.kind === 'proposal'" class="rd-prop" :class="'is-' + m.req_status">
              <div class="rd-item-top">
                <span class="rd-tile is-connect" aria-hidden="true"><svg class="rd-svg is-filled" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" /></svg></span>
                <StatusBadge :label="propLabel(m.req_status)" :tone="propTone(m.req_status)" />
                <span class="rd-item-when">{{ when }}</span>
              </div>
              <div class="rd-item-title">Connect proposes</div>
              <div class="rd-item-sub">{{ m.body }}</div>
              <template v-if="m.req_status === 'pending' && m.proposal && chat.me && m.proposal.asker === chat.me.uid">
                <DangerConfirm v-if="dangerOf(m)" :count="dangerOf(m)" :busy="deciding === m.id"
                               @confirm="typed => decide(m, 'confirm_delete', typed)" @cancel="decide(m, 'cancel')" />
                <div class="rd-actions" v-else>
                  <button type="button" class="rd-btn rd-btn--go" :disabled="deciding === m.id" @click="decide(m, 'confirm')">{{ assistant.needsApproval ? "Send for approval" : "Confirm" }}</button>
                  <button type="button" class="rd-btn" :disabled="deciding === m.id" @click="decide(m, 'cancel')">Cancel</button>
                </div>
              </template>
              <div class="rd-item-sub" v-else-if="m.req_status === 'pending'">Waiting for the person who asked.</div>
            </div>
            <div v-else-if="m.kind === 'request'" class="rd-req">
              <div class="rd-item-top">
                <span class="rd-tile" aria-hidden="true"><AppIcon name="ic-users" /></span>
                <StatusBadge :label="reqLabel(m.req_status)" :tone="reqTone(m.req_status)" />
                <span class="rd-item-when">{{ when }}</span>
              </div>
              <div class="rd-item-title">{{ m.from_name }} asks for <em>{{ m.req_role_name }}</em></div>
              <div class="rd-item-sub">“{{ m.body }}”</div>
              <div class="rd-actions" v-if="m.req_status === 'pending' && approvals.approver">
                <button type="button" class="rd-btn rd-btn--go" @click="setTab('approvals')">Decide in Approvals</button>
              </div>
            </div>
            <div v-else class="rd-msg" :class="{ 'is-mine': chat.me && m.from_uid === chat.me.uid, 'is-bot': m.bot, 'has-rich': hasRich(m) }">
              <div class="rd-msg-meta"><b>{{ m.from_name }}</b><span class="rd-bot-tag" v-if="m.bot">assistant</span><span class="rd-bot-tag" v-if="m.bot && activeEncrypted" title="The assistant is the server; its lines are not encrypted">plain</span> · {{ when }}
                <button type="button" class="rd-del" v-if="!m.deleted && (chat.me.moderator || m.from_uid === chat.me.uid)" title="Delete" @click="$store.dispatch('chatDelete', m.id)"><AppIcon name="x" /></button>
              </div>
              <div class="rd-msg-body" :class="{ 'is-deleted': m.deleted || m.locked }" :title="m.locked ? $tr('Encrypted with a key this browser does not have yet. It opens once a member with the key has the panel open.') : ''">
                <a v-if="isImageMsg(m)" :href="imageUrl(m)" target="_blank" rel="noopener"><img class="rd-msg-img" :src="imageUrl(m)" alt="uploaded image" /></a>
                <template v-else>{{ msgText(m) }}</template>
              </div>
              <!-- the charts, tables and files that came with Connect's reply -->
              <AssistantRich v-if="hasRich(m)" :rich="m.rich" />
            </div>
          </template>
          <div v-if="isAssistantChannel && assistant.busy" class="rd-thinking">
            <span class="rd-thinking-icon" aria-hidden="true"><svg class="rd-svg is-filled" viewBox="0 0 24 24"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" /><path d="M19 17l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z" /></svg></span>
            Connect is thinking…
          </div>
        </div>

        <!-- /clear: remove my whole conversation with Connect -->
        <ReasonConfirm
          v-if="slashClearAsk"
          class="rd-conv-confirm"
          title="Clear this conversation?"
          text="Your whole conversation with Connect is removed for you. This cannot be undone."
          confirmLabel="Clear"
          :askReason="false"
          danger
          @confirm="doClear"
          @cancel="slashClearAsk = false"
        />

        <!-- /model: which model answers me -->
        <AssistantModelPicker v-if="showModel && isAssistantChannel && assistant.modelCanChoose" @close="showModel = false" @picked="showModel = false" />

        <!-- New: talking with Connect, until it is tried or closed -->
        <div v-if="isAssistantChannel && canTalk && voiceHint && !talking && activeMessages.length" class="rd-voice-hint">
          <span class="rd-voice-orb rd-voice-orb--sm" aria-hidden="true"><svg class="rd-svg" viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></svg></span>
          <span class="rd-voice-hint-text"><b>New: talk with Connect</b> Ask out loud in any language; it answers by voice.</span>
          <button type="button" class="rd-voice-hint-go" @click="startTalking">Try it</button>
          <button type="button" class="rd-voice-hint-x" aria-label="Close" title="Close" @click="dismissVoiceHint">×</button>
        </div>

        <!-- Talking with Connect (the mic): Listening / Speaking, what each side said, Stop -->
        <AssistantVoice v-if="talking && isAssistantChannel" @close="talking = false" />

        <!-- Files (or /files): the PDF reports and Excel files Connect made for me -->
        <AssistantFilesPanel v-if="showFiles && isAssistantChannel" @close="showFiles = false" />

        <!-- /help: the commands, and a few things to try -->
        <div v-if="showHelp && isAssistantChannel" class="rd-help">
          <div class="rd-help-row"><b>Commands</b><button type="button" class="rd-link" @click="showHelp = false">Close</button></div>
          <div class="rd-help-cmd" v-for="c in commands" :key="c.cmd"><code>{{ c.cmd }}<template v-if="c.args">&nbsp;{{ $tr(c.args) }}</template></code> <span>{{ $tr(c.desc) }}</span></div>
          <div class="rd-help-row rd-help-row--next"><b>Try asking</b></div>
          <div class="rd-chips rd-chips--start" v-if="chips.length">
            <button type="button" class="rd-chip" v-for="c in chips" :key="c" :disabled="assistant.busy" @click="showHelp = false; draft = c; send()">{{ c }}</button>
          </div>
        </div>

        <!-- slash-command menu while typing "/" -->
        <div v-if="slashMenu.length" class="rd-slash">
          <button type="button" class="rd-slash-item" v-for="c in slashMenu" :key="c.cmd" @mousedown.prevent="runCommand(c.cmd)">
            <code>{{ c.cmd }}<template v-if="c.args">&nbsp;{{ $tr(c.args) }}</template></code><span>{{ $tr(c.desc) }}</span>
          </button>
        </div>

        <div class="rd-notice" v-if="isAssistantChannel && assistant.loaded && !assistant.enabled">The assistant is not switched on on this server.</div>
        <div class="rd-notice" v-else-if="activeEncrypted && chat.e2ee.status !== 'ready'">🔒 Finish the encryption setup above to write here.</div>
        <!-- the box: what is written on top, attach and Send underneath -->
        <div class="rd-composer" v-else>
          <textarea ref="composer" class="rd-composer-input" rows="2" v-model="draft" :placeholder="isAssistantChannel ? $tr('Ask Connect… Enter to send') : $tr('Write a message… Enter to send')" @keydown.enter.exact.prevent="onEnter"></textarea>
          <div class="rd-composer-bar">
            <label class="rd-attach" v-if="isAssistantChannel" title="Attach an image (JPEG or PNG)">
              <svg class="rd-svg" viewBox="0 0 24 24"><path d="M21.4 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.2-9.19a4 4 0 0 1 5.65 5.66l-9.2 9.19a2 2 0 0 1-2.82-2.83l8.48-8.48" /></svg>
              <input type="file" accept="image/jpeg,image/png" @change="attach" hidden />
            </label>
            <button type="button" class="rd-talk" v-if="isAssistantChannel && canTalk" :class="{ 'is-on': talking }" :aria-pressed="talking ? 'true' : 'false'"
                    :title="talking ? $tr('Stop talking') : $tr('Talk with Connect in any language')" @click="talking ? (talking = false) : startTalking()">
              <svg class="rd-svg" viewBox="0 0 24 24"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></svg>
              {{ talking ? "Stop" : "Talk" }}
            </button>
            <button type="button" class="rd-send" :disabled="!draft.trim() || (isAssistantChannel && assistant.busy)" @click="send">
              Send
              <svg class="rd-svg" viewBox="0 0 24 24"><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4 20-7z" /></svg>
            </button>
          </div>
        </div>
      </template>
    </div>
    </template>
  </aside>
</template>

<script>
import { mapState } from "vuex";
import { timeAgo } from "@/ui/labels";
import ChangeSetCard from "./ChangeSetCard.vue";
import DangerConfirm from "./DangerConfirm.vue";
import AssistantRich from "./AssistantRich.vue";
import AssistantFilesPanel from "./AssistantFilesPanel.vue";
import AssistantModelPicker from "./AssistantModelPicker.vue";
import AssistantVoice from "./AssistantVoice.vue";
import { voiceSupported } from "@/utils/liveVoice";
import { drawerFit } from "@/store/modules/common/panel";
import { tr, dateLocale } from "@/i18n";

const VOICE_HINT_KEY = "connect.voiceHint.v1";
/** The "New: talk with Connect" card shows until this browser tried voice or closed it. */
function voiceHintWanted() {
  try { return localStorage.getItem(VOICE_HINT_KEY) !== "1"; } catch (e) { return true; }
}

/**
 * The right-hand panel of the shell: what waits for the signed-in person's
 * approval, and the team chat. Its width is published as --drawer-width (see
 * store/modules/common/panel.js) so the content and the footer make room.
 */
export default {
  name: "RightDrawer",
  components: { ChangeSetCard, DangerConfirm, AssistantRich, AssistantFilesPanel, AssistantModelPicker, AssistantVoice },
  data() {
    return {
      pending: null, // access request { id, decision }
      pendingNotif: null, // notification { hash, decision }
      pendingChange: null, // Connect change waiting for an administrator { id, decision }
      pendingBlog: null, // website blog change waiting for an administrator { id, decision }
      withdrawing: null, // id of the person's own blog change being withdrawn
      switching: false,
      requestOpen: false, reqRole: "", reqReason: "",
      groupOpen: false, groupName: "", groupMembers: [], archiveAsk: false,
      draft: "",
      nearBottom: true,
      removing: "",
      deciding: null,
      resizing: false,
      e2eePass: "", e2eePass2: "", e2eeResetAsk: false, e2eeResetting: false,
      slashClearAsk: false, showHelp: false, showFiles: false, showModel: false, talking: false, voiceHint: voiceHintWanted(),
    };
  },
  computed: {
    ...mapState({
      open: (s) => s.panel.drawerOpen,
      tab: (s) => s.panel.drawerTab,
      chat: (s) => s.chat,
      requests: (s) => s.approvals.requests,
      notifs: (s) => s.approvals.notifications,
      changes: (s) => s.approvals.changes,
      blog: (s) => s.approvals.blog,
      assistant: (s) => s.assistant,
    }),
    approvals() { return this.requests; },
    rejectedMine() { return this.notifs.mine.filter((n) => n.approval === "rejected"); },
    approvalsCount() { return this.$store.getters.approvalsCount; },
    chatUnread() { return this.$store.getters.chatUnread; },
    channelList() { return this.chat.channels.filter((c) => c.kind !== "dm"); },
    // Connect's own conversation is listed only for who may talk to it (assistant.use).
    dmList() { return this.chat.channels.filter((c) => c.kind === "dm" && (!c.bot || this.assistant.allowed)); },
    activeMessages() { return this.chat.messages[this.chat.active] || []; },
    /** The visible list with its time labels made once; typing in the composer no longer reformats every row. */
    rows() { return this.activeMessages.map((m) => ({ m, when: this.clock(m.created_at) })); },
    activeName() {
      const c = this.chat.channels.find((x) => x.key === this.chat.active);
      return c ? c.name : this.chat.active;
    },
    activeIsGroup() { return /^grp:\d+$/.test(this.chat.active || ""); },
    assistantChannel() { return this.assistant.channel || (this.chat.me ? "dm:0:" + this.chat.me.uid : ""); },
    isAssistantChannel() { return !!this.assistantChannel && this.chat.active === this.assistantChannel; },
    chips() { return this.$store.getters.assistantChips; },
    /** The model answering me, for the head (empty until /assistant/models answered: an older API shows nothing). */
    modelName() {
      const m = this.assistant.models.find((x) => x.id === this.assistant.modelPick);
      return m ? m.name : "";
    },
    /**
     * Cards Connect made while "Ask before changes" is off (_auto, set by the server for an administrator's
     * own conversation): confirmed here as soon as they show, as if Confirm was pressed. A card that deletes
     * or takes things away never carries the mark: it still waits for the number.
     */
    autoCards() {
      const me = this.chat.me;
      if (!me || !this.assistantChannel) return [];
      return (this.chat.messages[this.assistantChannel] || []).filter((m) =>
        m.kind === "proposal" && m.req_status === "pending" && m.proposal && m.proposal.asker === me.uid &&
        m.proposal.args && m.proposal.args._auto && !this.dangerOf(m));
    },
    /**
     * Connect's slash commands. ask: the question sent to Connect (from what follows the command; ''
     * = the command needs it typed first). run: done here without the model, as a card's button does.
     */
    commands() {
      return [
        { cmd: "/today", desc: "Sales so far today", ask: () => "Sales so far today at this store (every store when none is picked): net sales, orders and average ticket at the till, website and app, and Uber, DoorDash and Skip as a separate figure." },
        { cmd: "/week", desc: "The last 7 days against the 7 before", ask: () => "The last 7 days against the 7 days before, at this store (every store when none is picked): net sales, orders and average ticket at the till, website and app, and Uber, DoorDash and Skip as a separate figure." },
        { cmd: "/pending", desc: "What is waiting for me", ask: () => "What is waiting for me: notifications and access requests waiting for approval, and suggested deals waiting for review." },
        { cmd: "/off", args: "[product]", desc: "What is off at this store, or mark a product sold out", ask: (p) => (p ? "Mark " + p + " as sold out at this store." : "Which menus and products are off or sold out at this store right now?") },
        { cmd: "/on", args: "<product>", desc: "Switch a product back on at this store", ask: (p) => (p ? "Switch " + p + " back on at this store." : "") },
        { cmd: "/undo", desc: "Undo my last change", run: "undo" },
        { cmd: "/sync", desc: "Send my last menu change to the tills", run: "sync" },
        { cmd: "/model", desc: "Which model answers you: Gemini or DeepSeek" },
        { cmd: "/files", desc: "The PDF reports and Excel files Connect made for you" },
        { cmd: "/clear", desc: "Clear this conversation" },
        { cmd: "/help", desc: "What Connect can do" },
      ].filter((c) => c.cmd !== "/model" || this.assistant.modelCanChoose);   // /model: administrators only
    },
    /** The command menu shown while the composer starts with "/" in Connect's chat. */
    slashMenu() {
      if (!this.isAssistantChannel || this.draft.charAt(0) !== "/") return [];
      const q = this.draft.slice(1).toLowerCase();
      return this.commands.filter((c) => c.cmd.slice(1).startsWith(q));
    },
    openTab() { return (this.open ? "1" : "0") + ":" + this.tab; },
    /** The mic shows where the browser can talk (a microphone, the audio thread) and Connect is on for me. */
    canTalk() { return voiceSupported() && this.assistant.allowed && this.assistant.enabled; },
    activeEncrypted() { return this.$store.getters.chatEncrypted(this.chat.active); },
    e2eeCard() { return this.chat.e2ee.enabled && ["setup", "unlock", "nocrypto"].indexOf(this.chat.e2ee.status) !== -1; },
  },
  watch: {
    // One watcher for open+tab: a single click sets both, and it must cost one round of requests, not two.
    async openTab(now, was) {
      const [openNow, tabNow] = now.split(":"); const wasTab = (was || "").split(":")[1];
      if (openNow !== "1" || tabNow !== "assistant") this.talking = false;   // voice ends with the drawer or the tab
      if (openNow === "1") {
        await this.$store.dispatch("chatInit");
        this.$store.dispatch("chatPoll");
        this.$store.dispatch("chatApprovals");
        if (tabNow === "assistant") this.openAssistant();
        else if (wasTab === "assistant") this.$store.commit("SET_CHAT_ACTIVE", "");
        else if (this.chat.active) this.$store.dispatch("chatMarkRead", this.chat.active);   // reopening on a channel: it is read now
      }
      this.$store.dispatch("chatSchedule");
    },
    // Deep, so a message's decrypted text arriving keeps the view at the bottom too: under Vue 2
    // the store's Vue.set on the message woke this watcher; Vue 3 only sees it through deep.
    activeMessages: {
      deep: true,
      handler() { this.$nextTick(() => { if (this.nearBottom) this.scrollBottom(); }); },
    },
    "chat.active"() { this.nearBottom = true; this.slashClearAsk = false; this.showHelp = false; this.showFiles = false; this.showModel = false; this.talking = false; this.$nextTick(this.scrollBottom); },
    autoCards(list) {
      list.forEach((m) => {
        if (this.autoConfirmed.has(m.id)) return;   // once per card, whatever the poll brings back
        this.autoConfirmed.add(m.id);
        this.decide(m, "confirm");
      });
    },
  },
  created() {
    // Not reactive on purpose: only remembers which cards were confirmed by themselves already.
    this.autoConfirmed = new Set();
  },
  methods: {
    timeAgo,
    setTab(tab) { this.$store.commit("SET_DRAWER", { tab }); },
    online(uid) { return this.$store.getters.chatOnline(uid); },
    openChannel(key) { this.$store.dispatch("chatOpen", key); },
    async archiveGroup() {
      this.archiveAsk = false;
      await this.$store.dispatch("chatArchiveGroup", this.chat.active);
    },
    reqLabel(s) { return s === "approved" ? tr("Approved") : s === "declined" ? tr("Declined") : tr("Waiting"); },
    notifLabel(a) { return a === "sent" ? tr("Sent") : a === "approved" ? tr("Approved") : a === "rejected" ? tr("Not approved") : tr("Waiting for approval"); },
    notifTone(a) { return a === "sent" ? "green" : a === "approved" ? "blue" : a === "rejected" ? "red" : "amber"; },
    async removeNotif(n) {
      this.removing = n.notification_hash;
      await this.$store.dispatch("removeNotification", n.notification_hash);
      this.removing = "";
    },
    async clearRejected() {
      for (const n of this.rejectedMine.slice()) await this.removeNotif(n);
    },
    async confirmChange(note) {
      const p = this.pendingChange;
      this.pendingChange = null;
      await this.$store.dispatch("decideChange", { id: p.id, decision: p.decision, note });
    },
    async confirmBlog(note) {
      const p = this.pendingBlog;
      this.pendingBlog = null;
      await this.$store.dispatch("decideBlogRequest", { id: p.id, decision: p.decision, note });
    },
    async withdrawBlog(b) {
      this.withdrawing = b.id;
      await this.$store.dispatch("withdrawBlogRequest", b.id);
      this.withdrawing = null;
    },
    /** The change on the Website blog page: what it changes, side by side with the website. */
    reviewBlog(b) {
      this.$router.push({ name: "website-blog", query: { request: String(b.id) } });
      if (this.$store.state.panel.mobile) this.$store.dispatch("closeDrawer");
    },
    blogLabel(s) { return s === "pending" ? tr("Waiting for approval") : s === "approved" ? tr("Approved") : s === "rejected" ? tr("Not approved") : s === "withdrawn" ? tr("Withdrawn") : tr("Closed"); },
    blogTone(s) { return s === "pending" ? "amber" : s === "approved" ? "green" : s === "rejected" ? "red" : "gray"; },
    async toggleSkipConfirm() {
      this.switching = true;
      await this.$store.dispatch("assistantSetSkipConfirm", !this.assistant.skipConfirm);
      this.switching = false;
    },
    async confirmNotif(note) {
      const p = this.pendingNotif;
      this.pendingNotif = null;
      if (p.decision === "approve") await this.$store.dispatch("approveNotification", p.hash);
      else await this.$store.dispatch("rejectNotification", { hash: p.hash, note });
    },
    reqTone(s) { return s === "approved" ? "green" : s === "declined" ? "red" : "amber"; },
    clock(ts) {
      if (!ts) return "";
      const d = new Date(String(ts).replace(" ", "T"));
      if (isNaN(d.getTime())) return ts;
      const today = new Date().toDateString() === d.toDateString();
      return (today ? "" : d.toLocaleDateString(dateLocale(), { month: "short", day: "numeric" }) + " ") + d.toLocaleTimeString(dateLocale(), { hour: "2-digit", minute: "2-digit" });
    },
    async confirmResolve(note) {
      const p = this.pending;
      this.pending = null;
      await this.$store.dispatch("chatResolve", { id: p.id, decision: p.decision, note });
    },
    async sendRequest() {
      const ok = await this.$store.dispatch("chatRequest", { role: this.reqRole, reason: this.reqReason });
      if (ok) { this.requestOpen = false; this.reqRole = ""; this.reqReason = ""; }
    },
    async createGroup() {
      const ok = await this.$store.dispatch("chatCreateGroup", { name: this.groupName, members: this.groupMembers });
      if (ok) { this.groupOpen = false; this.groupName = ""; this.groupMembers = []; }
    },
    /** Enter in Connect's composer: pick the top slash command if the menu is open, else send. */
    onEnter() {
      if (this.slashMenu.length) { this.runCommand(this.slashMenu[0].cmd); return; }
      this.send();
    },
    /** Run a slash command (from the menu or Enter); arg is what was typed after it. */
    runCommand(cmd, arg = "") {
      const c = this.commands.find((x) => x.cmd === cmd);
      if (!c) return;
      // Connect answers one thing at a time: the command stays in the box until the answer.
      if ((c.ask || c.run) && this.assistant.busy) return;
      const question = c.ask ? c.ask(arg) : "";
      this.focusComposer();
      if (c.ask && !question) { this.draft = cmd + " "; return; }   // it needs a product: type it after the command
      this.draft = "";
      if (c.run || c.ask) this.nearBottom = true;   // the answer or the new card shows at the bottom
      if (c.run) { this.$store.dispatch("assistantChangesetLatest", c.run); return; }
      if (c.ask) { this.ask(question); return; }
      if (cmd === "/clear") {
        if (!this.activeMessages.length) { this.showHelp = false; return; }
        this.showHelp = false;
        this.slashClearAsk = true;
      } else if (cmd === "/help") {
        this.slashClearAsk = false;
        this.showHelp = true;
      } else if (cmd === "/files") {
        this.showHelp = false;
        this.showModel = false;
        this.showFiles = true;
      } else if (cmd === "/model") {
        this.showHelp = false;
        this.showFiles = false;
        this.showModel = true;
      }
    },
    /** Voice from the Talk button, the welcome or the "new" card; once tried, the card stays away. */
    startTalking() {
      this.talking = true;
      this.dismissVoiceHint();
    },
    dismissVoiceHint() {
      this.voiceHint = false;
      try { localStorage.setItem(VOICE_HINT_KEY, "1"); } catch (e) { /* private window: it shows again next time */ }
    },
    async doClear() {
      this.slashClearAsk = false;
      await this.$store.dispatch("assistantClear");
    },
    async send() {
      const body = this.draft.trim();
      if (!body) return;
      // While Connect is thinking the next message can be typed, but it goes only after the answer.
      if (this.isAssistantChannel && this.assistant.busy) return;
      this.draft = "";
      this.nearBottom = true;
      // Keep typing without clicking back into the box (a click on Send takes the focus).
      this.focusComposer();
      if (this.isAssistantChannel) {
        if (body.charAt(0) === "/") {
          const word = body.split(/\s+/)[0];
          if (this.commands.some((c) => c.cmd === word.toLowerCase())) { this.runCommand(word.toLowerCase(), body.slice(word.length).trim()); return; }
          // an unknown /word is not a command: let Connect answer it like any message
        }
        await this.ask(body);
        return;
      }
      const channel = this.chat.active;
      const id = await this.$store.dispatch("chatSend", { key: channel, body });
      if (id && this.assistant.allowed && /@connect\b/i.test(body)) {
        // In an encrypted channel the server cannot read the mention: hand it the text and the recent talk, decrypted here.
        const enc = this.$store.getters.chatEncrypted(channel);
        this.$store.dispatch("assistantMention", { channel, id, text: enc ? body : "", context: enc ? this.$store.getters.chatContext(channel, id) : "" });
      }
    },
    propLabel(s) { return s === "pending" ? tr("Needs your confirmation") : s === "awaiting" ? tr("Waiting for an administrator") : s === "done" ? tr("Done") : s === "failed" ? tr("Failed") : s === "rejected" ? tr("Not approved") : s === "cancelled" ? tr("Cancelled") : s === "expired" ? tr("Expired") : s; },
    propTone(s) { return this.reqTone(s === "pending" || s === "awaiting" ? "pending" : s === "done" ? "approved" : "declined"); },
    async decide(m, decision, typed) {
      this.deciding = m.id;
      await this.$store.dispatch("assistantExecute", { id: m.id, decision, typed });
      this.deciding = null;
    },
    /** How many things a card deletes or takes away (the number to type), or 0. */
    dangerOf(m) {
      return Number((m.proposal && m.proposal.args && m.proposal.args._confirm) || 0);
    },
    /** A reply of Connect's that brought charts or files (message.rich). */
    hasRich(m) { return !!(m.bot && m.rich && !m.deleted); },
    isImageMsg(m) { return !m.deleted && /^\[image\] https?:\/\//.test(m.body || ""); },
    /** What a row shows: the decrypted text when there is one, a lock when the key is missing, the body when it is plain. */
    msgText(m) {
      if (m.deleted) return tr("message removed");
      if (m.text !== undefined && m.text !== null) return m.text;
      if (typeof m.body === "string" && m.body.slice(0, 3) === "e1.") return m.locked ? "🔒 " + tr("Encrypted — no key for it in this browser yet") : "…";
      return m.body;
    },
    async e2eeSetup(skipPassphrase) {
      const ok = await this.$store.dispatch("e2eeSetup", { passphrase: skipPassphrase ? "" : this.e2eePass, reset: this.e2eeResetting });
      if (ok) { this.e2eePass = ""; this.e2eePass2 = ""; this.e2eeResetting = false; }
    },
    async e2eeUnlock() {
      const ok = await this.$store.dispatch("e2eeUnlock", { passphrase: this.e2eePass });
      if (ok) this.e2eePass = "";
    },
    /** A new key while one exists elsewhere: the setup form again, posted as a reset. */
    e2eeReset() {
      this.e2eeResetAsk = false; this.e2eeResetting = true; this.e2eePass = ""; this.e2eePass2 = "";
      this.$store.commit("SET_E2EE", { status: "setup", error: "" });
    },
    imageUrl(m) { return String(m.body || "").slice(8).trim(); },
    attach(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      this.$store.dispatch("assistantUpload", { file, page: this.$route.name, store: this.$store.state.stores.current.storeHash });
      this.focusComposer();
    },
    /** A question to Connect, from the box or a command; it may answer by opening a page. */
    async ask(text) {
      const res = await this.$store.dispatch("assistantAsk", { text, page: this.$route.name, store: this.$store.state.stores.current.storeHash });
      if (res && res.route && res.route !== this.$route.name) this.$router.push({ name: res.route }).catch(() => {});
    },
    focusComposer() {
      if (this.$refs.composer) this.$refs.composer.focus();
    },
    /** Resize by dragging the left edge: width = distance from the pointer to the right edge of the window. */
    startResize(e) {
      this.resizing = true;
      const el = this.$el;
      // Beside the page it grows only as far as the page keeps its room; floating, to 70% of the window (panel.js).
      const { min, max } = drawerFit(this.$store.state.panel.sidebarCollapsed, this.$store.state.panel.drawerWidth);
      let pending = 0; let width = Math.round(el.getBoundingClientRect().width);
      // While dragging only the drawer's own inline width moves (one frame at a time);
      // the page-wide geometry (content padding, footer) is committed once on release.
      const move = (ev) => {
        const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
        width = Math.max(min, Math.min(max, window.innerWidth - x));
        if (pending) return;
        pending = requestAnimationFrame(() => { pending = 0; el.style.width = width + "px"; });
      };
      const stop = () => {
        this.resizing = false;
        if (pending) cancelAnimationFrame(pending);
        el.style.width = "";
        this.$store.commit("SET_DRAWER_WIDTH", { width, persist: true });
        window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", stop);
        window.removeEventListener("touchmove", move); window.removeEventListener("touchend", stop);
      };
      window.addEventListener("mousemove", move); window.addEventListener("mouseup", stop);
      window.addEventListener("touchmove", move, { passive: true }); window.addEventListener("touchend", stop);
      void e;
    },
    resetWidth() { this.$store.commit("SET_DRAWER_WIDTH", { width: 360, persist: true }); },
    /** The assistant tab is its direct message, opened straight away. */
    async openAssistant() {
      if (!this.assistant.loaded) await this.$store.dispatch("assistantStatus");
      if (!this.assistant.allowed) { this.setTab("chat"); return; }   // given by hand: no way in without assistant.use
      if (!this.chat.me) await this.$store.dispatch("chatInit");
      if (this.assistantChannel) this.$store.dispatch("chatOpen", this.assistantChannel);
      if (!this.assistant.models.length) this.$store.dispatch("assistantModels");
    },
    onScroll() {
      const el = this.$refs.conv;
      if (!el) return;
      this.nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    },
    scrollBottom() {
      const el = this.$refs.conv;
      if (el) el.scrollTop = el.scrollHeight;
    },
  },
};
</script>

<style scoped>
/* The look of the Online Orders page (surfaces.css) at the drawer's size: cards with an icon tile,
   tinted pills, pill buttons, room to breathe. Theme tokens only, and nothing moves: closed, the
   drawer waits off-screen and opens at once. */
.rd { position: fixed; top: var(--topbar-height); right: 0; bottom: 0; width: var(--drawer-width-open); z-index: 24; display: flex; flex-direction: column; background: var(--bg-sidebar); border-left: 1px solid var(--sidebar-border); transform: translateX(100%); }
.rd.is-open { transform: translateX(0); }
/* Too narrow for the page and the drawer side by side (panel.js CONTENT_MIN): the drawer floats over the page. */
.rd.is-floating { z-index: 40; box-shadow: var(--shadow-overlay); }
@media (max-width: 900px) {
  .rd { width: min(100vw, 420px); bottom: 0; z-index: 40; box-shadow: var(--shadow-overlay); }
}
.rd-grip { position: absolute; top: 0; bottom: 0; left: -3px; width: 7px; cursor: col-resize; z-index: 2; }
.rd-grip:hover, .rd.is-resizing .rd-grip { background: var(--accent-primary); opacity: 0.5; }
.rd.is-resizing { user-select: none; }
.rd-svg { fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.rd-svg.is-filled { fill: currentColor; stroke-width: 1.5; }

/* head: the tab's mark, its name and state, then the tabs as chips */
.rd-head { flex: 0 0 auto; display: flex; flex-direction: column; gap: var(--s-4); padding: var(--s-4) var(--s-4) var(--s-3); border-bottom: 1px solid var(--sidebar-border); container-type: inline-size; }
.rd-head-top { display: flex; align-items: center; gap: var(--s-3); }
.rd-head-icon { width: 40px; height: 40px; flex: none; display: flex; align-items: center; justify-content: center; border-radius: 12px; color: var(--btn-primary-text); background: linear-gradient(135deg, var(--brand-orange), #fdba74); box-shadow: 0 8px 20px -10px var(--brand-orange); }
.rd-head-icon svg { width: 20px; height: 20px; }
.rd-head-text { flex: 1; min-width: 0; }
.rd-head-title { font-size: var(--t-heading-sm); font-weight: 650; letter-spacing: -0.01em; line-height: 1.3; color: var(--text-primary); }
.rd-head-state { margin-top: 2px; font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rd-state-dot { display: inline-block; width: 7px; height: 7px; margin: 0 6px 1px 0; border-radius: 50%; vertical-align: middle; background: var(--text-muted); }
.rd-state-dot.is-good { background: var(--success); }
.rd-state-dot.is-warn { background: var(--warning); }
.rd-state-dot.is-busy { background: var(--brand-orange); }
.rd-close { width: 34px; height: 34px; flex: none; display: inline-flex; align-items: center; justify-content: center; padding: 0; border: 1px solid var(--border-primary); border-radius: 999px; background: transparent; color: var(--text-muted); cursor: pointer; }
.rd-close:hover { background: var(--bg-hover); color: var(--text-primary); }
.rd-close svg { width: 16px; height: 16px; }
.rd-tabs { display: flex; flex-wrap: wrap; gap: 6px; }
.rd-tab { display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 14px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-secondary); color: var(--text-secondary); font: inherit; font-size: var(--t-body-sm); font-weight: 500; white-space: nowrap; cursor: pointer; }
.rd-tab:hover { color: var(--text-primary); border-color: var(--brand-orange); }
.rd-tab.is-on, .rd-tab.is-on:hover { background: var(--brand-orange); border-color: var(--brand-orange); color: var(--btn-primary-text); font-weight: 600; }
.rd-count, .rd-dot { box-sizing: border-box; min-width: 20px; height: 20px; padding: 0 6px; flex: none; border-radius: 999px; background: var(--brand-orange); color: var(--btn-primary-text); font-size: var(--t-caption); font-weight: 600; line-height: 20px; text-align: center; font-variant-numeric: tabular-nums; }
.rd-tab.is-on .rd-count { background: var(--bg-primary); color: var(--brand-orange); }
/* dragged down towards its 320px minimum, the drawer keeps its tabs on one row */
@container (max-width: 320px) {
  .rd-tabs { gap: 4px; }
  .rd-tab { gap: 5px; padding: 0 10px; }
}

/* body */
.rd-body { flex: 1; overflow-y: auto; padding: var(--s-5) var(--s-4) var(--s-6); background: var(--bg-secondary); }
.rd-body--chat { display: flex; flex-direction: column; padding: var(--s-4); }
.rd-lede { margin: 0; font-size: var(--t-body-sm); line-height: 1.6; color: var(--text-muted); }
.rd-section-title { display: flex; align-items: center; gap: var(--s-2); margin: var(--s-6) 0 var(--s-3); font-size: var(--t-caption); font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); }
.rd-section-title:first-child, .rd-e2ee + .rd-section-title { margin-top: 0; }
.rd-lede + .rd-section-title { margin-top: var(--s-5); }
.rd-section-title .rd-link { margin-left: auto; }
.rd-section-count { box-sizing: border-box; min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px; background: var(--accent-primary-bg); color: var(--text-link); font-size: var(--t-caption); font-weight: 600; line-height: 20px; letter-spacing: 0; text-align: center; font-variant-numeric: tabular-nums; }
.rd-muted { font-size: var(--t-body-sm); line-height: 1.55; color: var(--text-muted); }
.rd-error { display: flex; align-items: flex-start; gap: var(--s-2); padding: var(--s-3) var(--s-4); border-radius: 14px; background: var(--danger-bg); color: var(--text-primary); font-size: var(--t-body-sm); line-height: 1.5; }
.rd-error svg { width: 16px; height: 16px; flex: none; margin-top: 1px; color: var(--danger); }
.rd-empty { display: flex; align-items: center; gap: var(--s-3); padding: var(--s-3) var(--s-4); border: 1px dashed var(--border-primary); border-radius: 14px; font-size: var(--t-body-sm); line-height: 1.5; color: var(--text-muted); }
.rd-empty-mark { width: 28px; height: 28px; flex: none; display: flex; align-items: center; justify-content: center; border-radius: 50%; color: var(--success); background: var(--success-bg); }
.rd-empty-mark svg { width: 15px; height: 15px; stroke-width: 2.5; }

/* cards: what waits for a yes, and the history under it */
.rd-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--s-3); }
.rd-item, .rd-prop, .rd-req { padding: var(--s-4); background: var(--bg-primary); border: 1px solid var(--border-primary); border-radius: var(--card-radius, 16px); box-shadow: var(--card-shadow); }
.rd-list--quiet .rd-item { box-shadow: none; }
.rd-item-top { display: flex; align-items: center; gap: var(--s-2); margin-bottom: var(--s-3); }
.rd-tile { width: 32px; height: 32px; flex: none; display: flex; align-items: center; justify-content: center; border-radius: 10px; color: var(--brand-orange); background: var(--accent-primary-bg); }
.rd-tile svg { width: 16px; height: 16px; }
.rd-tile.is-connect { color: var(--btn-primary-text); background: linear-gradient(135deg, var(--brand-orange), #fdba74); }
.rd-item-when { margin-left: auto; font-size: var(--t-caption); color: var(--text-muted); white-space: nowrap; }
.rd-item-title { font-size: var(--t-body); font-weight: 600; line-height: 1.45; color: var(--text-primary); overflow-wrap: anywhere; }
.rd-item-title em { font-style: normal; color: var(--text-link); }
.rd-item-sub { margin-top: var(--s-1); font-size: var(--t-body-sm); line-height: 1.55; color: var(--text-secondary); overflow-wrap: anywhere; }
.rd-item-sub b { font-weight: 600; color: var(--text-primary); }
.rd-item-warn { display: flex; align-items: flex-start; gap: var(--s-2); margin-top: var(--s-3); padding: 10px 12px; border-radius: 12px; background: var(--warning-bg); color: var(--text-secondary); }
.rd-item-warn svg { width: 16px; height: 16px; flex: none; margin-top: 1px; color: var(--warning); }
.rd-item-img { display: block; max-width: 100%; max-height: 8rem; margin-top: var(--s-3); border: 1px solid var(--border-light); border-radius: 12px; }
.rd-confirm { margin-top: var(--s-3); }
.rd-actions { display: flex; flex-wrap: wrap; gap: var(--s-2); margin-top: var(--s-4); }
.rd-btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 34px; padding: 6px 16px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-primary); color: var(--text-secondary); font: inherit; font-size: var(--t-body-sm); font-weight: 600; line-height: 1.3; cursor: pointer; }
.rd-btn:hover:not(:disabled) { border-color: var(--brand-orange); color: var(--text-primary); }
.rd-btn:disabled { opacity: 0.45; cursor: default; }
.rd-btn--go { background: var(--btn-primary-bg); border-color: var(--btn-primary-bg); color: var(--btn-primary-text); }
.rd-btn--go:hover:not(:disabled) { background: var(--btn-primary-hover-bg); border-color: var(--btn-primary-hover-bg); color: var(--btn-primary-hover-text); }
.rd-btn--sm { min-height: 28px; padding: 4px 12px; font-size: var(--t-caption); }
.rd-link { padding: 0; border: 0; background: transparent; font: inherit; font-size: var(--t-caption); font-weight: 600; letter-spacing: 0; text-transform: none; color: var(--text-link); cursor: pointer; }
.rd-link:hover { text-decoration: underline; }

/* channel list */
.rd-chan { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 2px; }
.rd-chan-row { display: flex; align-items: center; gap: var(--s-3); min-height: 44px; padding: 6px 10px; border-radius: 12px; cursor: pointer; }
.rd-chan-row:hover { background: var(--bg-hover); }
.rd-chan-row:focus-visible { outline: 2px solid var(--brand-orange); outline-offset: -2px; }
.rd-chan-mark { width: 32px; height: 32px; flex: none; display: flex; align-items: center; justify-content: center; border-radius: 10px; background: var(--bg-tertiary); color: var(--text-secondary); font-size: 15px; font-weight: 600; }
.rd-chan-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: var(--t-body); color: var(--text-primary); }
.rd-chan-row.is-unread .rd-chan-name { font-weight: 600; }
.rd-chan-kind { flex: 0 1 auto; max-width: 40%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: var(--t-caption); color: var(--text-muted); }
.rd-avatar { position: relative; width: 32px; height: 32px; flex: none; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: var(--accent-primary-bg); color: var(--text-link); font-size: var(--t-body-sm); font-weight: 600; text-transform: uppercase; }
.rd-avatar.is-bot { color: var(--btn-primary-text); background: linear-gradient(135deg, var(--brand-orange), #fdba74); }
.rd-presence { position: absolute; right: -1px; bottom: -1px; width: 10px; height: 10px; border-radius: 50%; background: var(--border-secondary); box-shadow: 0 0 0 2px var(--bg-secondary); }
.rd-presence.is-on { background: var(--success); }
.rd-e2ee-note { margin-top: var(--s-4); padding: var(--s-2) var(--s-3); border-radius: 12px; background: var(--bg-tertiary); font-size: var(--t-caption); line-height: var(--t-caption-lh); }
.rd-foot-actions { display: flex; flex-wrap: wrap; gap: var(--s-2); margin-top: var(--s-5); }

/* forms: encryption, asking for a role, a new group */
.rd-form { margin-top: var(--s-4); padding: var(--s-4); display: flex; flex-direction: column; gap: var(--s-3); background: var(--bg-primary); border: 1px solid var(--border-primary); border-radius: var(--card-radius, 16px); box-shadow: var(--card-shadow); }
.rd-form .rd-actions { margin-top: 0; }
.rd-form-head { display: flex; align-items: center; gap: var(--s-3); }
.rd-form-title { font-size: 15px; font-weight: 650; letter-spacing: -0.01em; color: var(--text-primary); }
.rd-input { width: 100%; box-sizing: border-box; padding: 10px 14px; border: 1px solid var(--border-primary); border-radius: 12px; background: var(--bg-input); color: var(--text-primary); font: inherit; font-size: var(--t-body); resize: vertical; }
.rd-input:focus { outline: none; border-color: var(--border-input-focus); background: var(--bg-primary); box-shadow: 0 0 0 3px var(--accent-primary-bg); }
.rd-check-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px var(--s-3); max-height: 10rem; overflow-y: auto; }
.rd-check { display: flex; align-items: center; gap: var(--s-2); font-size: var(--t-body-sm); color: var(--text-primary); }
.rd-check input { accent-color: var(--brand-orange); }
.rd-e2ee { margin: 0 0 var(--s-5); border-color: var(--accent-primary); }
.rd-e2ee p { margin: 0; }
.rd-e2ee-help { padding-top: var(--s-3); border-top: 1px solid var(--border-light); font-size: var(--t-caption); line-height: 1.6; }
.rd-e2ee-help b { color: var(--text-secondary); font-weight: 600; }

/* conversation */
.rd-conv-head { flex: 0 0 auto; display: flex; flex-wrap: wrap; align-items: center; gap: var(--s-2) var(--s-3); margin: 0 0 var(--s-1); padding-bottom: var(--s-3); border-bottom: 1px solid var(--border-primary); }
.rd-back { width: 32px; height: 32px; flex: none; display: inline-flex; align-items: center; justify-content: center; padding: 0; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-primary); color: var(--text-primary); cursor: pointer; }
.rd-back:hover { border-color: var(--brand-orange); }
.rd-back svg { width: 16px; height: 16px; }
.rd-conv-name { flex: 1 1 180px; min-width: 0; font-size: 15px; font-weight: 650; line-height: 1.45; color: var(--text-primary); overflow-wrap: anywhere; }
/* Connect's conversation: the name on its own line, its switches (Ask before changes, model, Files) together below it */
.rd-conv-head.is-assistant .rd-conv-name { flex-basis: 100%; }
.rd-bot-sub { font-size: var(--t-caption); font-weight: 400; color: var(--text-muted); }
.rd-conv-act { flex: none; color: var(--danger); }
.rd-switch { flex: none; display: inline-flex; align-items: center; gap: 6px; height: 28px; padding: 0 12px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-primary); color: var(--text-secondary); font: inherit; font-size: var(--t-caption); font-weight: 600; white-space: nowrap; cursor: pointer; }
.rd-switch::before { content: ""; width: 7px; height: 7px; border-radius: 50%; background: var(--success); }
.rd-switch.is-off { border-color: var(--accent-primary); background: var(--accent-primary-bg); color: var(--text-link); }
.rd-switch.is-off::before { background: var(--brand-orange); }
.rd-switch:disabled { opacity: 0.6; cursor: default; }
.rd-conv-confirm { flex: 0 0 auto; margin: var(--s-2) 0; }
/* the conversation reaches the drawer's edges, so card shadows and the scroll bar are not cut */
.rd-conv { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: var(--s-3); margin: 0 calc(-1 * var(--s-4)); padding: var(--s-3) var(--s-4); }
.rd-more { align-self: center; padding: 4px 12px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-primary); color: var(--text-muted); font: inherit; font-size: var(--t-caption); font-weight: 600; cursor: pointer; }
.rd-more:hover { border-color: var(--brand-orange); color: var(--text-primary); }
.rd-conv-empty { margin: auto 0; padding: var(--s-5) 0; text-align: center; font-size: var(--t-body-sm); color: var(--text-muted); }
.rd-welcome { margin: auto 0; display: flex; flex-direction: column; align-items: center; gap: var(--s-4); padding: var(--s-5) var(--s-2); text-align: center; }
.rd-welcome-icon { width: 52px; height: 52px; display: flex; align-items: center; justify-content: center; border-radius: 14px; color: var(--btn-primary-text); background: linear-gradient(135deg, var(--brand-orange), #fdba74); box-shadow: 0 10px 24px -10px var(--brand-orange); }
.rd-welcome-icon svg { width: 26px; height: 26px; }
.rd-welcome-text { margin: 0; max-width: 34ch; font-size: var(--t-body-sm); line-height: 1.6; color: var(--text-muted); }
.rd-chips { display: flex; flex-wrap: wrap; justify-content: center; gap: var(--s-2); }
.rd-chips--start { justify-content: flex-start; }
.rd-chip { max-width: 100%; padding: 8px 14px; border: 1px solid var(--border-primary); border-radius: 18px; background: var(--bg-primary); color: var(--text-secondary); font: inherit; font-size: var(--t-body-sm); line-height: 1.4; text-align: left; cursor: pointer; }
.rd-chip:hover:not(:disabled) { border-color: var(--brand-orange); color: var(--text-primary); }
.rd-chip:disabled { opacity: 0.5; cursor: default; }
.rd-sys { align-self: center; max-width: 92%; padding: 6px 14px; border-radius: 12px; background: var(--bg-tertiary); color: var(--text-muted); font-size: var(--t-caption); line-height: var(--t-caption-lh); text-align: center; }
.rd-prop.is-pending { border-color: var(--accent-primary); }
.rd-prop.is-failed, .rd-prop.is-cancelled, .rd-prop.is-expired { opacity: 0.8; }
.rd-msg { max-width: 85%; align-self: flex-start; display: flex; flex-direction: column; align-items: flex-start; }
.rd-msg.is-mine { align-self: flex-end; align-items: flex-end; }
.rd-msg.has-rich { max-width: 100%; width: 100%; }
.rd-files-btn::before { content: none; }
.rd-files-btn .rd-svg { width: 13px; height: 13px; }
.rd-files-btn.is-on { border-color: var(--accent-primary); background: var(--accent-primary-bg); color: var(--text-link); }
.rd-msg-meta { margin: 0 6px 4px; font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); }
.rd-msg-meta b { font-weight: 600; color: var(--text-secondary); }
.rd-bot-tag { display: inline-block; margin-left: 6px; padding: 0 7px; border-radius: 999px; background: var(--accent-primary-bg); color: var(--text-link); font-size: var(--t-caption); font-weight: 600; line-height: 18px; }
.rd-del { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; margin-left: 2px; padding: 0; border: 0; border-radius: 999px; background: transparent; color: var(--text-muted); vertical-align: middle; cursor: pointer; }
.rd-del:hover { background: var(--danger-bg); color: var(--danger); }
.rd-del svg { width: 12px; height: 12px; }
.rd-msg-body { max-width: 100%; padding: 10px 14px; border: 1px solid var(--border-primary); border-radius: 18px 18px 18px 6px; background: var(--bg-primary); color: var(--text-primary); font-size: var(--t-body); line-height: 1.55; white-space: pre-wrap; word-break: break-word; box-shadow: var(--shadow-sm); }
.rd-msg.is-mine .rd-msg-body { border-color: transparent; border-radius: 18px 18px 6px 18px; background: var(--accent-primary-bg); box-shadow: none; }
.rd-msg-body.is-deleted { font-style: italic; color: var(--text-muted); }
.rd-msg-img { display: block; max-width: 220px; max-height: 160px; border-radius: 12px; }
.rd-thinking { align-self: flex-start; display: inline-flex; align-items: center; gap: var(--s-2); padding: 6px 14px 6px 6px; border: 1px solid var(--border-primary); border-radius: 999px; background: var(--bg-primary); color: var(--text-muted); font-size: var(--t-body-sm); }
.rd-thinking-icon { width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; border-radius: 50%; color: var(--btn-primary-text); background: linear-gradient(135deg, var(--brand-orange), #fdba74); }
.rd-thinking-icon svg { width: 13px; height: 13px; }

/* /help and the slash menu */
.rd-help { flex: 0 0 auto; display: flex; flex-direction: column; gap: var(--s-2); margin: var(--s-2) 0 0; padding: var(--s-4); background: var(--bg-primary); border: 1px solid var(--border-primary); border-radius: var(--card-radius, 16px); box-shadow: var(--card-shadow); }
.rd-help-row { display: flex; align-items: center; justify-content: space-between; font-size: var(--t-caption); letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); }
.rd-help-row b { font-weight: 600; }
.rd-help-row--next { margin-top: var(--s-3); }
.rd-help-cmd { font-size: var(--t-body-sm); line-height: 1.5; color: var(--text-secondary); }
.rd-help-cmd code, .rd-slash-item code { padding: 1px 8px; border-radius: 999px; background: var(--accent-primary-bg); color: var(--text-link); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: var(--t-caption); }
.rd-slash { flex: 0 0 auto; margin: var(--s-2) 0 0; overflow: hidden; background: var(--bg-primary); border: 1px solid var(--border-primary); border-radius: 14px; box-shadow: var(--card-shadow); }
.rd-slash-item { display: flex; align-items: center; gap: var(--s-3); width: 100%; padding: 10px 14px; border: 0; background: transparent; color: var(--text-primary); font: inherit; font-size: var(--t-body-sm); text-align: left; cursor: pointer; }
.rd-slash-item + .rd-slash-item { border-top: 1px solid var(--border-light); }
.rd-slash-item:hover { background: var(--bg-hover); }
.rd-slash-item span { color: var(--text-muted); }

/* the composer: a rounded box, Send in the brand colour */
.rd-notice { flex: 0 0 auto; margin-top: var(--s-3); padding: var(--s-3) var(--s-4); border-radius: 14px; background: var(--bg-tertiary); color: var(--text-muted); font-size: var(--t-body-sm); line-height: 1.5; text-align: center; }
.rd-composer { flex: 0 0 auto; display: flex; flex-direction: column; margin-top: var(--s-3); background: var(--bg-primary); border: 1px solid var(--border-primary); border-radius: 18px; box-shadow: var(--card-shadow); }
.rd-composer:focus-within { border-color: var(--border-input-focus); box-shadow: 0 0 0 3px var(--accent-primary-bg), var(--card-shadow); }
.rd-composer-input { display: block; width: 100%; min-width: 0; box-sizing: border-box; padding: 12px 16px 4px; border: 0; border-radius: 18px 18px 0 0; background: transparent; color: var(--text-primary); font: inherit; font-size: var(--t-body); line-height: 1.5; resize: none; }
.rd-composer-input:focus, .rd-composer-input:focus-visible { outline: none; border: 0; background: transparent; box-shadow: none; }
.rd-composer-input::placeholder { color: var(--text-muted); }
.rd-composer-bar { display: flex; align-items: center; gap: var(--s-2); padding: 4px 8px 8px; }
.rd-attach { width: 34px; height: 34px; flex: none; display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; color: var(--text-muted); cursor: pointer; }
.rd-attach:hover { background: var(--bg-hover); color: var(--brand-orange); }
.rd-attach svg { width: 17px; height: 17px; }
.rd-talk { display: inline-flex; align-items: center; gap: 6px; height: 34px; padding: 0 14px 0 12px; border: 0; border-radius: 999px; color: var(--btn-primary-text);
  background: linear-gradient(135deg, var(--brand-orange), #fdba74); box-shadow: 0 6px 16px -8px var(--brand-orange); font: inherit; font-size: var(--t-body-sm); font-weight: 650; cursor: pointer; }
.rd-talk svg { width: 16px; height: 16px; }
.rd-talk:hover { box-shadow: 0 8px 20px -8px var(--brand-orange); filter: saturate(1.1); }
.rd-talk.is-on { color: var(--brand-orange); background: var(--accent-primary-bg); box-shadow: inset 0 0 0 1px var(--brand-orange); }
.rd-voice-orb { width: 44px; height: 44px; flex: none; display: inline-flex; align-items: center; justify-content: center; border-radius: 999px; color: var(--btn-primary-text);
  background: linear-gradient(135deg, var(--brand-orange), #fdba74); box-shadow: 0 10px 24px -10px var(--brand-orange), 0 0 0 6px var(--accent-primary-bg); }
.rd-voice-orb svg { width: 20px; height: 20px; }
.rd-voice-orb--sm { width: 32px; height: 32px; box-shadow: 0 0 0 4px var(--accent-primary-bg); }
.rd-voice-orb--sm svg { width: 16px; height: 16px; }
.rd-welcome-talk { display: flex; align-items: center; gap: 14px; width: 100%; max-width: 320px; padding: 14px 16px; border: 1px solid var(--brand-orange-light); border-radius: 16px; text-align: left;
  background: linear-gradient(135deg, var(--accent-primary-bg), transparent 70%), var(--bg-primary); font: inherit; color: var(--text-primary); cursor: pointer; }
.rd-welcome-talk:hover { border-color: var(--brand-orange); }
.rd-welcome-talk-text { display: flex; flex-direction: column; gap: 2px; }
.rd-welcome-talk-text b { font-size: var(--t-body); font-weight: 650; }
.rd-welcome-talk-text small { font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); }
.rd-voice-hint { flex: 0 0 auto; display: flex; align-items: center; gap: 10px; margin-top: var(--s-2); padding: 10px 10px 10px 12px; border: 1px solid var(--brand-orange-light); border-radius: 14px;
  background: linear-gradient(135deg, var(--accent-primary-bg), transparent 75%), var(--bg-primary); }
.rd-voice-hint-text { flex: 1; min-width: 0; font-size: var(--t-caption); line-height: var(--t-caption-lh); color: var(--text-muted); }
.rd-voice-hint-text b { display: block; font-size: var(--t-body-sm); font-weight: 650; color: var(--text-primary); }
.rd-voice-hint-go { flex: none; height: 30px; padding: 0 12px; border: 0; border-radius: 999px; color: var(--btn-primary-text); background: linear-gradient(135deg, var(--brand-orange), #fdba74); font: inherit; font-size: var(--t-caption); font-weight: 650; cursor: pointer; }
.rd-voice-hint-x { flex: none; width: 24px; height: 24px; padding: 0; border: 0; border-radius: 999px; background: transparent; color: var(--text-muted); font-size: 16px; line-height: 1; cursor: pointer; }
.rd-voice-hint-x:hover { background: var(--bg-hover); color: var(--text-primary); }
.rd-send { display: inline-flex; align-items: center; gap: 6px; height: 34px; margin-left: auto; padding: 0 14px 0 16px; border: 0; border-radius: 999px; background: var(--btn-primary-bg); color: var(--btn-primary-text); font: inherit; font-size: var(--t-body-sm); font-weight: 600; cursor: pointer; }
.rd-send:hover:not(:disabled) { background: var(--btn-primary-hover-bg); color: var(--btn-primary-hover-text); }
.rd-send:disabled { opacity: 0.45; cursor: default; }
.rd-send svg { width: 15px; height: 15px; }
</style>
